import React, { useEffect, useState } from "react";
import {
  Drawer,
  Stack,
  Group,
  Title,
  Text,
  TextInput,
  Textarea,
  Switch,
  Button,
  Image,
  FileButton,
  Divider,
  TagsInput,
  Tabs,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconUpload,
  IconTrash,
  IconLanguage,
  IconPhoto,
  IconNotes,
  IconGlass,
} from "@tabler/icons-react";
import type { Menu } from "./types";
import {
  uploadMenuImage,
  deleteMenuImage,
  uploadCategoryTopImage,
  deleteCategoryTopImage,
  uploadOnboardingModalImage,
  deleteOnboardingModalImage,
} from "../../../../services/imageService";

interface Props {
  menu: Menu;
  opened: boolean;
  onClose: () => void;
  onUpdate: (updates: Partial<Menu>) => Promise<void>;
  onRefetch: () => void;
}

const ImageField: React.FC<{
  label: string;
  hint: string;
  url: string | null | undefined;
  busy: boolean;
  onUpload: (file: File) => Promise<void>;
  onDelete: () => Promise<void>;
}> = ({ label, hint, url, busy, onUpload, onDelete }) => (
  <Stack gap={6}>
    <Group justify="space-between">
      <div>
        <Text size="sm" fw={500}>
          {label}
        </Text>
        <Text size="xs" c="dimmed">
          {hint}
        </Text>
      </div>
      <Group gap={6}>
        <FileButton
          accept="image/*"
          onChange={(file) => file && onUpload(file)}
        >
          {(p) => (
            <Button
              {...p}
              size="compact-xs"
              variant="default"
              leftSection={<IconUpload size={12} />}
              loading={busy}
            >
              {url ? "Replace" : "Upload"}
            </Button>
          )}
        </FileButton>
        {url && (
          <Tooltip label="Remove">
            <ActionIcon
              size="sm"
              variant="subtle"
              color="red"
              onClick={onDelete}
              loading={busy}
            >
              <IconTrash size={14} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
    </Group>
    {url && (
      <Image
        src={url}
        alt={label}
        radius="md"
        style={{ maxHeight: 140, objectFit: "cover" }}
      />
    )}
  </Stack>
);

export const MenuSettingsDrawer: React.FC<Props> = ({
  menu,
  opened,
  onClose,
  onUpdate,
  onRefetch,
}) => {
  // Local drafts so typing doesn't trigger menu-level patches every keystroke
  const [name, setName] = useState(menu.name || "");
  const [adminNotes, setAdminNotes] = useState(menu.adminNotes || "");
  const [drinkEnabled, setDrinkEnabled] = useState(
    menu.drinkSection?.enabled || false
  );
  const [drinkCategories, setDrinkCategories] = useState<string[]>(
    menu.drinkSection?.drinkCategories || []
  );
  const [imageBusy, setImageBusy] = useState<string | null>(null);

  // Reset drafts when drawer opens
  useEffect(() => {
    if (opened) {
      setName(menu.name || "");
      setAdminNotes(menu.adminNotes || "");
      setDrinkEnabled(menu.drinkSection?.enabled || false);
      setDrinkCategories(menu.drinkSection?.drinkCategories || []);
    }
  }, [opened, menu]);

  const saveName = async () => {
    if (name.trim() === (menu.name || "")) return;
    await onUpdate({ name: name.trim() || null });
  };

  const saveNotes = async () => {
    if (adminNotes === (menu.adminNotes || "")) return;
    await onUpdate({ adminNotes });
  };

  const saveDrinkSection = async () => {
    const next = { enabled: drinkEnabled, drinkCategories };
    const prev = menu.drinkSection || { enabled: false, drinkCategories: [] };
    if (
      next.enabled === prev.enabled &&
      JSON.stringify(next.drinkCategories) ===
        JSON.stringify(prev.drinkCategories)
    ) {
      return;
    }
    await onUpdate({ drinkSection: next });
  };

  const uploadImage = async (
    kind: "hero" | "category-top" | "onboarding",
    file: File
  ) => {
    setImageBusy(kind);
    try {
      if (kind === "hero") await uploadMenuImage(menu.id, file);
      else if (kind === "category-top")
        await uploadCategoryTopImage(menu.id, file);
      else await uploadOnboardingModalImage(menu.id, file);
      onRefetch();
    } catch (e: any) {
      notifications.show({
        color: "red",
        title: "Upload failed",
        message: e?.message || "Unknown error",
      });
    } finally {
      setImageBusy(null);
    }
  };

  const removeImage = async (kind: "hero" | "category-top" | "onboarding") => {
    setImageBusy(kind);
    try {
      if (kind === "hero") await deleteMenuImage(menu.id);
      else if (kind === "category-top") await deleteCategoryTopImage(menu.id);
      else await deleteOnboardingModalImage(menu.id);
      onRefetch();
    } catch (e: any) {
      notifications.show({
        color: "red",
        title: "Delete failed",
        message: e?.message || "Unknown error",
      });
    } finally {
      setImageBusy(null);
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title={
        <Title order={4} fw={600}>
          Menu settings
        </Title>
      }
    >
      <Tabs defaultValue="general">
        <Tabs.List mb="md">
          <Tabs.Tab value="general" leftSection={<IconNotes size={14} />}>
            General
          </Tabs.Tab>
          <Tabs.Tab value="branding" leftSection={<IconPhoto size={14} />}>
            Branding
          </Tabs.Tab>
          <Tabs.Tab value="drink" leftSection={<IconGlass size={14} />}>
            Drink section
          </Tabs.Tab>
          <Tabs.Tab value="welcome" leftSection={<IconLanguage size={14} />}>
            Welcome modal
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="general">
          <Stack gap="md">
            <TextInput
              label="Menu name"
              description='Short name shown in the QR code panel dropdown, e.g. "Summer 2025" or "Lunch menu". Not shown to diners.'
              placeholder="e.g. Dinner menu"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              onBlur={saveName}
            />
            <Textarea
              label="Admin notes"
              description="Internal notes about this menu. Not shown to diners."
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.currentTarget.value)}
              onBlur={saveNotes}
              autosize
              minRows={4}
            />
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="branding">
          <Stack gap="lg">
            <ImageField
              label="Menu hero image"
              hint="Shown at the top of the menu (1600 × 900)"
              url={menu.imageUrl}
              busy={imageBusy === "hero"}
              onUpload={(f) => uploadImage("hero", f)}
              onDelete={() => removeImage("hero")}
            />
            <Divider />
            <ImageField
              label="Category modal top image"
              hint="Background image for the category modal (1600 × 900)"
              url={menu.categoryTopImageUrl}
              busy={imageBusy === "category-top"}
              onUpload={(f) => uploadImage("category-top", f)}
              onDelete={() => removeImage("category-top")}
            />
            <Divider />
            <ImageField
              label="Onboarding modal image"
              hint="Shown on the welcome modal (1200 × 900)"
              url={menu.onboardingModalImageUrl}
              busy={imageBusy === "onboarding"}
              onUpload={(f) => uploadImage("onboarding", f)}
              onDelete={() => removeImage("onboarding")}
            />
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="drink">
          <Stack gap="md">
            <Switch
              label="Enable dedicated drink section"
              description="When on, the listed categories appear in a separate drinks section."
              checked={drinkEnabled}
              onChange={(e) => setDrinkEnabled(e.currentTarget.checked)}
            />
            <TagsInput
              label="Drink categories"
              description="Categories that should appear under Drinks."
              value={drinkCategories}
              onChange={setDrinkCategories}
              disabled={!drinkEnabled}
              placeholder="Add a category…"
            />
            <Group>
              <Button onClick={saveDrinkSection} size="sm">
                Save drink section
              </Button>
            </Group>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="welcome">
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              The welcome modal editor is complex and remains in the legacy
              editor for now. We'll port it here in a follow-up.
            </Text>
            <Button
              variant="default"
              component="a"
              href={`/admin/menus/edit/${menu.id}`}
            >
              Open in legacy editor
            </Button>
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Drawer>
  );
};
